import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../workspace/WorkspaceProvider';
import { useWorkspaceKind, isFirmWorkspace } from '../workspace/useWorkspaceKind';
import { isVisitingAdvisor } from '../firstrun/welcome';
import { useAdvisorFirm } from './useAdvisorFirm';
import { STEWARD_ROLES } from '../api/types';

/**
 * The advisor portal's Clients roster (docs/25 §2, S7) — the one place every exit from a
 * client frame lands (steward-app#1401).
 *
 * A single named constant rather than a literal at each call site: `WorkspaceSwitcher.tsx`
 * (picking the firm from the switcher), `Layout.tsx`'s `advisorNavItems` (the firm-workspace
 * nav's own Clients link) and every exit this hook exposes all need the SAME path, and a
 * literal repeated at each of those sites is a value four places would need to change in
 * step if the route ever moves. It already has, once: `/advisor/clients` since
 * steward-app#1405 (2026-09-11), which made Overview the `/advisor` index and moved Clients
 * off it — this constant is what kept that move to one line here rather than four.
 */
export const ADVISOR_CLIENTS_PATH = '/advisor/clients';

export interface ClientFrame {
  /** True whenever the façade has placed the caller at a firm outright OR is asking
   *  which one (steward-app#983's `firmChoice`) — see {@link useClientFrame}'s header.
   *  Exposed mainly for `Layout.tsx`'s firm-workspace nav, which must show NOTHING
   *  (not even the advisor items) while this is false, even if `inFirmWorkspace` is
   *  already true from the pack alone (steward-app#1271's cold-load case: the pack is
   *  mounted but this caller's own `GET /advisor/firms` hasn't resolved to anything
   *  yet). `inClientFrame` already folds this in; a firm-workspace consumer needs it
   *  on its own. */
  advisorNavVisible: boolean;
  /** True while the ACTIVE workspace is the advisor's own firm workspace — see
   *  {@link useClientFrame}'s header for how this differs from "not viewing a client". */
  inFirmWorkspace: boolean;
  /** True when there is no active membership, or its packs read is degraded
   *  (`packsResolved === false`), unless `inFirmWorkspace` already says firm. Then the workspace
   *  is not known to be a family's, though `inFirmWorkspace` reads `false`. The firm lookup is
   *  deliberately not consulted. The AI consent gate (steward-app#2523) reads this instead of
   *  guessing family, and nothing else changes: `inFirmWorkspace` keeps its own failure direction. */
  kindUnresolved: boolean;
  /** True while the active workspace is NEITHER the firm's own NOR (per
   *  `advisorNavVisible`) definitely someone else's — i.e. "not standing in the firm
   *  workspace, and the advisor portal is on for this caller". **This is broader than
   *  "looking at a client"**: it is equally true of a firm's own staff member standing
   *  in THEIR OWN personal family workspace (steward-app#1401 correction; see
   *  `inClientFrame` for the narrower, correct test). Kept, unchanged in meaning, only
   *  because #1402 (the Credentials hold) keys on it — no #1401 surface reads this
   *  field directly any more. */
  inClientWorkspace: boolean;
  /** The actual "advisor looking in on a client" state (steward-app#1401 correction) —
   *  every #1401 surface (the band, the nav's exit item, the "Clients" breadcrumb
   *  prefix, `ViewOnlyPill`'s suppression, and `AdvisorClients.tsx`'s tab-only
   *  persistence on Open) is driven by THIS, never by `inClientWorkspace` alone.
   *
   *  `inClientWorkspace && isVisitingAdvisor(activeMembership)`: the extra test is
   *  what tells an actual client's household apart from the firm's own staff member
   *  standing in their OWN family workspace, which `inClientWorkspace` alone cannot —
   *  `Layout.tsx`'s pre-#1401 `clientSectionLabel` comment already named this exact
   *  ambiguity ("a RIPL staff member may hold their own personal Steward family
   *  workspace, which hits this exact branch ... exactly as often as an actual
   *  client's workspace does"). Without it the band would tell that person, in their
   *  own household, "Viewing {their own family} as their advisor. Read-only." — false
   *  on both halves, and the exits would pull them out of their own workspace with no
   *  way back except the switcher.
   *
   *  `isVisitingAdvisor` (`src/firstrun/welcome.ts`) is reused rather than a second
   *  role check written here: its docblock's "shape 3" edge (the advisor role unioned
   *  with an admin-capable one, steward-app#1087) resolves to "not visiting", which is
   *  the right answer here for the same reason — "Read-only" would be false for a
   *  membership that also holds an ordinary admin role. `isVisitingAdvisor` under-reports
   *  two further shapes (documented at its own definition); this hook inherits that,
   *  deliberately, rather than re-deriving a stricter test that could disagree with it.
   *
   *  Left FALSE while `activeMembership` hasn't loaded yet (`isVisitingAdvisor(null)` is
   *  `false`) — the same "omit, don't half-render" rule `clientName` follows. */
  inClientFrame: boolean;
  /** The workspace name to show in the band and the nav label while `inClientFrame`.
   *  Null (never shown empty) until the active membership's name is loaded. */
  clientName: string | null;
  /** Whether the band's "Read-only." clause is TRUE for this membership (correction, this
   *  branch's own security review). `isVisitingAdvisor` (and so `inClientFrame`) admits
   *  `STEWARD_ROLES.ADVISOR_CONTRIBUTOR` as an advisor-scope role — deliberately, per its
   *  own header, since a contributor is still visiting rather than a member of the
   *  household. But a contributor genuinely holds `inst:write` (`packages/steward-types`'
   *  `roles.ts`, the "one deliberate exception" it names): they may add documents to the
   *  one shared folder their firm is named on (`useCanContributeToSharedFolder`). Saying
   *  "Read-only." to that membership is false on the one thing it can actually do, so the
   *  band omits the clause entirely for it rather than assert something narrower and still
   *  wrong in one line — see `ClientFrameBand`'s own render for the two copy branches this
   *  produces. Meaningless (and left `true`, harmlessly) outside `inClientFrame`. */
  clientReadOnly: boolean;
  /** Leave the client frame: switches the active workspace back to the advisor's own firm
   *  and navigates to {@link ADVISOR_CLIENTS_PATH}, in one step (see its own header for why
   *  a two-transition switch-then-navigate is wrong here). Every REAL exit affordance (the
   *  band's Back action, the nav's exit item, the "Clients" breadcrumb) calls this same
   *  function, so the three cannot disagree about where they land.
   *
   *  ⚠️ **Browser back is a CORRECTION, not a fourth exit that calls this directly** —
   *  `AdvisorClients.tsx`'s `openClient` pushes a history entry rather than replacing it so
   *  back has somewhere to land, but nothing listens for the pop itself; landing anywhere
   *  under `/advisor` with the client workspace still active is caught a beat later by
   *  `RequireAdvisorFirm`'s own effect (steward-app#1401 review — it wraps the whole
   *  subtree, not just Clients), which calls this SAME function. Its `replace: true` also
   *  drops the stale page's forward-history entry, which is the point, not a side effect to
   *  route around — see `docs/04-navigation-ia.md` §4.5.
   *
   *  Falls back to a plain navigation, with no workspace switch, when `advisorFor` is null:
   *  the ambiguous multi-firm case (steward-app#983, `firmChoice` present) has no single
   *  firm workspace id to switch to, and `/advisor` itself is what raises
   *  `RequireAdvisorFirm`'s chooser for that caller. This is pre-existing behaviour for that
   *  case (an advisor item's plain link always did a same-workspace navigation here), not a
   *  new hybrid state: it only ever applies while the caller has not yet picked a firm to
   *  act as, never after they are actually standing in a resolved client's workspace. */
  exitToClients: () => void;
}

/**
 * One source of truth for "is this session inside a client's workspace, looking in on it as
 * an advisor" (steward-app#1401), read by every piece of chrome that needs to say so:
 * `SidebarContent`'s nav frame, the client band, `ViewOnlyPill`'s suppression, and the
 * "Clients / {name}" breadcrumb prefix. Extracted from `Layout.tsx`'s `SidebarContent`,
 * where four separate consumers used to each recompute (or risk disagreeing about)
 * `inFirmWorkspace` / `inClientWorkspace` — the shape #1401 replaces is documented at
 * length in that file's git history if you need the case-by-case reasoning; what follows is
 * the current, load-bearing account.
 *
 * ⚠️ **`advisorNavVisible` is not "is an advisor" and `inFirmWorkspace` is not its
 * negation** — read both branches below before changing either in isolation.
 *
 * `advisorFor` null does not mean "not an advisor" (steward-app#983): a caller staff at more
 * than one registered firm who has not said which one they are working as gets `advisorFor:
 * null` WITH a `firmChoice`, and treating that as "not an advisor" would strand them with no
 * way to reach the chooser `RequireAdvisorFirm` renders for either firm. So the advisor
 * portal is considered "on" for this caller (`advisorNavVisible`) whenever the façade has
 * placed them at a firm outright OR is asking which one.
 *
 * `inFirmWorkspace` answers a different question — "is the ACTIVE workspace the firm's own"
 * — and deliberately does NOT require `advisorNavVisible` (steward-app#1271): before #1271
 * it did, which meant an advisor whose `GET /advisor/firms` had not resolved yet stood in
 * their own firm workspace and read as "not in the firm workspace" (because the outer gate
 * was still false), which is what let the client frame render OVER the firm workspace by
 * accident. It is computed by the shared `isFirmWorkspace` helper (`workspace/useWorkspaceKind.ts`,
 * steward-app#736, 2026-09-23 — replacing an inline copy of the same expression that
 * `Settings.tsx`, `RequireFamilyWorkspace.tsx` and `HomeRoute.tsx` also carried, closing the
 * #1307 drift risk between the four by construction): `workspaceKind === 'firm'` is the
 * signal once `activeMembership` is known, and a narrow stand-in on
 * `advisorFor.firmWorkspaceId === activeWorkspaceId` covers only the window before it is —
 * see that helper's own header for the dated account of why the window exists
 * (`useAdvisorFirm()`'s workspace-less query can resolve before `activeMembership` does) and
 * why the stand-in must not outlive it.
 *
 * ⚠️ **`inClientWorkspace` (`advisorNavVisible && !inFirmWorkspace`) is NOT "looking at a
 * client"** (correction, steward-app#1401 review). It is also true of the firm's own staff
 * member standing in their OWN personal family workspace — an advisor is a person first,
 * and nothing about "not the firm workspace" tells that case apart from a real client's
 * household. `inClientFrame` is the narrower, correct test for every #1401 surface; see its
 * own doc above. `inClientWorkspace` is kept, meaning exactly what it always has, only
 * because #1402 (the Credentials hold) keys on it.
 */
export function useClientFrame(): ClientFrame {
  const { advisorFor, firmChoice } = useAdvisorFirm();
  const { activeWorkspaceId, activeMembership, switchWorkspace } = useWorkspace();
  const workspaceKind = useWorkspaceKind();
  const navigate = useNavigate();

  const advisorNavVisible = !!advisorFor || !!firmChoice;
  const inFirmWorkspace = isFirmWorkspace(workspaceKind, activeMembership, activeWorkspaceId, advisorFor);
  // Deliberately stricter than `isKnownFamilyWorkspace`: a degraded packs read stays unresolved
  // even once the firm lookup has settled, because that lookup can say "no match" for a firm
  // member (no `advisorFor`, or another firm's under `firmChoice`) while the server, which reads
  // the packs fresh, answers as a firm. Only a resolved packs read, or the stand-in arm
  // positively saying firm, ends the unresolved state.
  const kindUnresolved = !inFirmWorkspace && (!activeMembership || activeMembership.packsResolved === false);
  const inClientWorkspace = advisorNavVisible && !inFirmWorkspace;
  const inClientFrame = inClientWorkspace && isVisitingAdvisor(activeMembership);

  // Deliberately the workspace name ONLY, never "Client: <name>" — an advisor is a person
  // first, and (though `inClientFrame` now excludes their own family) the wording still
  // avoids implying a relationship this hook does not verify (`GET /advisor/clients` is
  // the only thing that could, and steward-app#661 deliberately keeps that fan-out off
  // this render path).
  const clientName = activeMembership?.workspaceName.trim() || null;
  const clientReadOnly = !(activeMembership?.roleIds ?? []).includes(STEWARD_ROLES.ADVISOR_CONTRIBUTOR);

  const exitToClients = useCallback(() => {
    if (advisorFor) {
      switchWorkspace(advisorFor.firmWorkspaceId, ADVISOR_CLIENTS_PATH);
    } else {
      // See exitToClients's own doc above: the ambiguous multi-firm case has no single
      // firm workspace to switch to yet.
      navigate(ADVISOR_CLIENTS_PATH);
    }
  }, [advisorFor, switchWorkspace, navigate]);

  return {
    advisorNavVisible, inFirmWorkspace, kindUnresolved, inClientWorkspace, inClientFrame, clientName, clientReadOnly, exitToClients,
  };
}
